'use client';

import React, { useState } from 'react';
import { ShowModal } from '../Modal';
import { Button } from '../ui/button';
import { Star, Upload, X, Loader2 } from 'lucide-react';
import { api } from 'src/services/api';
import { toast } from 'react-toastify';

interface ProductReviewModalProps {
  visible: boolean;
  onSetVisible: (visible: boolean) => void;
  productId: string;
  productName: string;
  productImage?: string;
  onReviewSubmitted?: () => void;
}

export const ProductReviewModal: React.FC<ProductReviewModalProps> = ({
  visible,
  onSetVisible,
  productId,
  productName,
  productImage,
  onReviewSubmitted,
}) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const MAX_IMAGES = 5;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const resetForm = () => {
    setRating(0);
    setHoveredRating(0);
    setTitle('');
    setDescription('');
    setImages([]);
    setImagePreviews([]);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onSetVisible(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const currentImageCount = images.length;
    const remainingSlots = MAX_IMAGES - currentImageCount;

    if (remainingSlots <= 0) {
      toast.error(`Você pode adicionar no máximo ${MAX_IMAGES} imagens`);
      return;
    }

    const filesToAdd = Array.from(files).slice(0, remainingSlots);
    const validFiles: File[] = [];
    const newPreviews: string[] = [];

    filesToAdd.forEach((file) => {
      // Validar tipo de arquivo
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name}: Formato inválido. Use JPG ou PNG`);
        return;
      }

      // Validar tamanho
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: Tamanho máximo de 5MB excedido`);
        return;
      }

      validFiles.push(file);

      // Criar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result as string);
        if (newPreviews.length === validFiles.length) {
          setImagePreviews((prev) => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });

    if (validFiles.length > 0) {
      setImages((prev) => [...prev, ...validFiles]);
      toast.success(
        `${validFiles.length} ${
          validFiles.length === 1 ? 'imagem adicionada' : 'imagens adicionadas'
        }`
      );
    }

    // Resetar input
    e.target.value = '';
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Falha ao ler arquivo'));
      reader.readAsDataURL(file);
    });
  };

  const validateForm = () => {
    if (rating === 0) {
      toast.error('Por favor, selecione uma classificação');
      return false;
    }

    if (!title.trim()) {
      toast.error('Por favor, insira um título para sua avaliação');
      return false;
    }

    if (title.trim().length < 3) {
      toast.error('O título deve ter pelo menos 3 caracteres');
      return false;
    }

    if (!description.trim()) {
      toast.error('Por favor, descreva sua experiência com o produto');
      return false;
    }

    if (description.trim().length < 10) {
      toast.error('A descrição deve ter pelo menos 10 caracteres');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      // Converter todas as imagens para base64
      const imagesBase64 = await Promise.all(
        images.map((img) => fileToBase64(img))
      );

      const payload = {
        stars: rating,
        name: title.trim(),
        title: title.trim(),
        description: description.trim(),
        image: imagesBase64.length > 0 ? imagesBase64[0] : '', // Primeira imagem
        images: imagesBase64, // Todas as imagens (caso backend suporte)
      };

      await api.put(`/review/create/${productId}`, payload);

      toast.success('Avaliação enviada com sucesso!');
      resetForm();
      onSetVisible(false);
      onReviewSubmitted?.();
    } catch (error: any) {
      console.error('Erro ao enviar avaliação:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Erro ao enviar avaliação. Tente novamente.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ShowModal
      visible={visible}
      onSetVisible={handleClose}
      title="Avaliar Produto"
      showCloseButton
    >
      <div className="p-4 space-y-5">
        {/* Informações do Produto */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          {productImage ? (
            <img
              src={productImage}
              alt={productName}
              className="w-16 h-16 object-cover rounded-md"
            />
          ) : (
            <div className="w-16 h-16 bg-gray-200 rounded-md flex items-center justify-center">
              <Upload className="w-6 h-6 text-gray-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900 text-sm line-clamp-2">
              {productName}
            </h3>
          </div>
        </div>

        {/* Classificação por Estrelas */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Classificação <span className="text-red-600">*</span>
          </label>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                disabled={isSubmitting}
                className="transition-transform hover:scale-110 disabled:cursor-not-allowed"
              >
                <Star
                  className={`w-8 h-8 ${
                    star <= (hoveredRating || rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
            {rating > 0 && (
              <span className="ml-2 text-sm text-gray-600">
                {rating} {rating === 1 ? 'estrela' : 'estrelas'}
              </span>
            )}
          </div>
        </div>

        {/* Título */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Título da avaliação <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Resuma sua experiência em poucas palavras"
            maxLength={100}
            disabled={isSubmitting}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <div className="text-xs text-gray-500 text-right">
            {title.length}/100
          </div>
        </div>

        {/* Descrição */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Sua avaliação <span className="text-red-600">*</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Compartilhe sua experiência com o produto..."
            maxLength={1000}
            rows={4}
            disabled={isSubmitting}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <div className="text-xs text-gray-500 text-right">
            {description.length}/1000
          </div>
        </div>

        {/* Upload de Imagens */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Adicionar fotos (opcional)
            <span className="text-gray-500 font-normal ml-1">
              - máximo {MAX_IMAGES} fotos
            </span>
          </label>

          {/* Grid de previews */}
          {imagePreviews.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-3">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="relative group">
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    disabled={isSubmitting}
                    className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-lg opacity-0 group-hover:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-1 right-1 bg-black bg-opacity-60 text-white text-xs px-1.5 py-0.5 rounded">
                    {index + 1}/{imagePreviews.length}
                  </div>
                </div>
              ))}

              {/* Botão para adicionar mais */}
              {images.length < MAX_IMAGES && (
                <label
                  className={`flex flex-col items-center justify-center h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Upload className="w-6 h-6 text-gray-400 mb-1" />
                  <span className="text-xs text-gray-500">Adicionar</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    multiple
                    onChange={handleImageChange}
                    disabled={isSubmitting}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          )}

          {/* Área de upload inicial */}
          {imagePreviews.length === 0 && (
            <label
              className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  Clique para adicionar fotos
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  JPG ou PNG • Máx. 5MB por foto • Até {MAX_IMAGES} fotos
                </p>
              </div>
              <input
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                multiple
                onChange={handleImageChange}
                disabled={isSubmitting}
                className="hidden"
              />
            </label>
          )}

          {/* Contador de imagens */}
          {images.length > 0 && (
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>
                {images.length} de {MAX_IMAGES}{' '}
                {images.length === 1 ? 'foto adicionada' : 'fotos adicionadas'}
              </span>
              {images.length >= MAX_IMAGES && (
                <span className="text-orange-600 font-medium">
                  Limite atingido
                </span>
              )}
            </div>
          )}
        </div>

        {/* Botões de Ação */}
        <div className="space-y-3 pt-2">
          <Button
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              rating === 0 ||
              !title.trim() ||
              !description.trim()
            }
            className="w-full bg-red-600 hover:bg-red-700 text-white h-11 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando avaliação...
              </>
            ) : (
              'Enviar Avaliação'
            )}
          </Button>

          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 h-11 bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </Button>
        </div>
      </div>
    </ShowModal>
  );
};
